import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

// Oblique-Style Drilldown Menu
const DrilldownMenu = ({ isOpen, onClose, anchorRect, menuTree, onSelect }) => {
    const [path, setPath] = useState(['root']);
    const [search, setSearch] = useState('');
    const menuRef = useRef(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (isOpen) { setPath(['root']); setSearch(''); }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) onClose();
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose]);

    if (!isOpen || !anchorRect) return null;

    const currentMenuId = path[path.length - 1];
    const currentMenu = menuTree[currentMenuId];
    if (!currentMenu) return null;

    return (
        <AnimatePresence>
            <motion.div
                ref={menuRef}
                initial={{ opacity: 0, y: 4, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.15 }}
                style={{
                    position: 'absolute',
                    top: anchorRect.bottom + 4,
                    left: anchorRect.left,
                    width: '280px',
                    background: '#ffffff',
                    borderRadius: '10px',
                    border: '1px solid var(--border)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    zIndex: 10000,
                    overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    maxHeight: '400px'
                }}
            >
                {path.length > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px' }}>
                        <button 
                            onClick={() => setPath(prev => prev.slice(0, -1))}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-tertiary)', display: 'flex' }}
                            className="hover-bg-subtle"
                        >
                            <ObliqueIcon name="arrow_back" size={16} />
                        </button>
                        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{currentMenu.title}</span>
                    </div>
                )}
                
                {currentMenu.searchable !== false && (
                    <div style={{ padding: '8px 12px 4px' }}>
                        <input
                            autoFocus
                            placeholder={`Search ${currentMenu.title ? currentMenu.title.toLowerCase() : 'options'}...`}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ width: '100%', padding: '6px 10px', background: 'var(--bg-subtle)', border: 'none', borderRadius: '6px', fontSize: '13px', color: 'var(--text-primary)', outline: 'none' }}
                        />
                    </div>
                )}

                <div style={{ padding: '4px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <AnimatePresence mode="wait">
                        <motion.div key={currentMenuId} initial={{ x: 10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -10, opacity: 0 }} transition={{ duration: 0.15 }} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {currentMenu.items.filter(item => item.type === 'header' || item.label.toLowerCase().includes(search.toLowerCase())).map((item, idx) => (
                                item.type === 'header' ? (
                                    <div key={idx} style={{ fontSize: '11px', fontWeight: '500', color: 'var(--text-tertiary)', padding: '10px 10px 4px', textTransform: 'uppercase', letterSpacing: '0.02em' }}>{item.label}</div>
                                ) : (
                                    <button
                                        key={idx}
                                        onClick={() => {
                                            if (item.nextMenu) { setPath(prev => [...prev, item.nextMenu]); setSearch(''); }
                                            else { onSelect(item.value, item); onClose(); }
                                        }}
                                        className="hover-bg-subtle"
                                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', textAlign: 'left', color: 'var(--text-primary)', fontSize: '13px' }}
                                    >
                                        <span style={{ color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', width: '16px', justifyContent: 'center' }}>
                                            {item.icon}
                                        </span>
                                        <span style={{ flex: 1 }}>{item.label}</span>
                                        {item.isNew && <span style={{ fontSize: '10px', background: '#e0f2fe', color: '#0284c7', padding: '2px 6px', borderRadius: '4px', fontWeight: '500' }}>New</span>}
                                        {item.nextMenu && <ObliqueIcon name="chevron_right" size={16} color="var(--text-tertiary)" />}
                                    </button>
                                )
                            ))}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

// Formula Builder exactly like the screenshot
const FormulaBuilderModal = ({ isOpen, onClose, onSave }) => {
    const [formula, setFormula] = useState('');
    const [activeVar, setActiveVar] = useState('whoever_triggered');

    if (!isOpen) return null;

    const docContent = {
        'whoever_triggered': {
            title: 'Whoever triggered', icon: 'people',
            desc: 'The user who made the edit that triggered this automation to run.',
            examples: ['@Whoever triggered', '@Whoever triggered.email()', '@Whoever triggered.name() == "Alice"']
        },
        'page_creator': { title: 'Page creator', icon: 'person_add', desc: 'The user who originally created the page.', examples: ['@Page creator'] },
        'time_triggered': { title: 'Time triggered', icon: 'schedule', desc: 'The exact time this automation was triggered.', examples: ['@Time triggered'] },
        'date_triggered': { title: 'Date triggered', icon: 'event', desc: 'The date this automation was triggered.', examples: ['@Date triggered'] },
        'trigger_page': { title: 'Trigger page', icon: 'arrow_outward', desc: 'The page that triggered the automation.', examples: ['@Trigger page.title'] },
        'not': { title: 'not', icon: 'check_box_outline_blank', desc: 'Inverts a boolean value.', examples: ['not(true) == false'] },
        'dateAdd': { title: 'dateAdd', icon: 'functions', desc: 'Adds time to a date.', examples: ['dateAdd(now(), 1, "days")'] }
    };

    const properties = ['whoever_triggered', 'page_creator', 'time_triggered', 'date_triggered', 'trigger_page'];
    const builtins = ['not', 'dateAdd'];

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.2)' }}>
            <motion.div
                initial={{ opacity: 0, scale: 0.98, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: 10 }}
                transition={{ duration: 0.15 }}
                style={{ width: '760px', background: '#ffffff', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
                    <button onClick={() => onSave(formula)} style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Save</button>
                </div>
                <div style={{ padding: '16px' }}>
                    <input autoFocus value={formula} onChange={e => setFormula(e.target.value)} placeholder="Your formula" style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: '14px', fontFamily: '"Fira Code", monospace' }} />
                </div>
                <div style={{ height: '1px', background: 'var(--border-subtle)' }} />
                <div style={{ display: 'flex', height: '360px' }}>
                    <div style={{ width: '240px', borderRight: '1px solid var(--border-subtle)', overflowY: 'auto', padding: '8px 0' }} className="custom-scrollbar">
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', padding: '8px 16px', fontWeight: '500' }}>Properties</div>
                        {properties.map(id => (
                            <button key={id} onClick={() => setActiveVar(id)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 16px', background: activeVar === id ? 'var(--bg-subtle)' : 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'left' }}>
                                <ObliqueIcon name={docContent[id].icon} size={15} color="var(--text-tertiary)" />
                                <span style={{ flex: 1 }}>{docContent[id].title}</span>
                                {activeVar === id && <ObliqueIcon name="keyboard_return" size={14} color="var(--text-tertiary)" />}
                            </button>
                        ))}
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', padding: '16px 16px 8px', fontWeight: '500' }}>Built-ins</div>
                        {builtins.map(id => (
                            <button key={id} onClick={() => setActiveVar(id)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 16px', background: activeVar === id ? 'var(--bg-subtle)' : 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'left' }}>
                                {id === 'not' ? <ObliqueIcon name="check_box_outline_blank" size={15} color="var(--text-tertiary)" /> : <ObliqueIcon name="functions" size={15} color="var(--text-tertiary)" />}
                                <span style={{ flex: 1 }}>{docContent[id].title}</span>
                                {activeVar === id && <ObliqueIcon name="keyboard_return" size={14} color="var(--text-tertiary)" />}
                            </button>
                        ))}
                    </div>
                    <div style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
                        {docContent[activeVar] && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <ObliqueIcon name={docContent[activeVar].icon} size={18} color="var(--text-tertiary)" />
                                    <span style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>{docContent[activeVar].title}</span>
                                </div>
                                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', margin: 0 }}>{docContent[activeVar].desc}</p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                                    {docContent[activeVar].examples.map((ex, i) => (
                                        <div key={i} style={{ padding: '10px 14px', border: '1px solid var(--border-subtle)', borderRadius: '8px', fontSize: '12px', fontFamily: '"Fira Code", monospace', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ whiteSpace: 'pre-wrap' }}>{ex}</span>
                                            <ObliqueIcon name="content_copy" size={14} color="var(--text-tertiary)" style={{ cursor: 'pointer' }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

// Main Builder exactly matching the screenshots
const AutomationBuilder = ({ isOpen, onClose, activeDatabaseName = "Projects" }) => {
    const [automationName, setAutomationName] = useState("Daniel Canosa's automation");
    const [triggers, setTriggers] = useState([]);
    const [actions, setActions] = useState([]);

    const [activeMenu, setActiveMenu] = useState(null); // 'trigger' or 'action'
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [showFormula, setShowFormula] = useState(false);

    // Exact trigger menu from screenshot 2
    const triggerMenuTree = {
        root: {
            title: "Triggers",
            searchable: true,
            items: [
                { type: 'header', label: 'Event' },
                { label: "Any property edited", icon: <ObliqueIcon name="edit" size={15} />, value: { type: 'any_property' } },
                { label: "Page added", icon: <ObliqueIcon name="add" size={15} />, value: { type: 'page_added' } },
                { type: 'header', label: 'Property' },
                { label: "Name", icon: <ObliqueIcon name="text_fields" size={15} />, nextMenu: 'prop_name' },
                { label: "Date", icon: <ObliqueIcon name="event" size={15} />, nextMenu: 'prop_date' },
                { label: "Status", icon: <ObliqueIcon name="radio_button_unchecked" size={15} />, nextMenu: 'prop_status' },
                { label: "Owner", icon: <ObliqueIcon name="people" size={15} />, nextMenu: 'prop_owner' },
                { label: "Dates", icon: <ObliqueIcon name="date_range" size={15} />, nextMenu: 'prop_dates' },
                { label: "Blocked By", icon: <ObliqueIcon name="remove_circle_outline" size={15} />, nextMenu: 'prop_blocked' },
                { label: "Priority", icon: <ObliqueIcon name="adjust" size={15} />, nextMenu: 'prop_priority' },
            ]
        },
        prop_status: {
            title: "Status set to", searchable: false,
            items: [
                { label: "Any option", icon: <ObliqueIcon name="check_box" size={15} color="#3b82f6" />, value: { type: 'status_edited' } },
                { label: "To-do", icon: <ObliqueIcon name="radio_button_unchecked" size={15} />, value: { type: 'status_todo' } },
                { label: "In progress", icon: <ObliqueIcon name="play_circle_outline" size={15} color="#3b82f6" />, value: { type: 'status_inprogress' } },
                { label: "Complete", icon: <ObliqueIcon name="check_circle_outline" size={15} color="#22c55e" />, value: { type: 'status_complete' } },
            ]
        },
        prop_date: {
            title: "Date changed", searchable: false,
            items: [
                { label: "Any change", icon: <ObliqueIcon name="swap_horiz" size={15} />, value: { type: 'date_changed' } }
            ]
        }
    };

    // Exact action menu from screenshot 3
    const actionMenuTree = {
        root: {
            title: "Actions",
            searchable: true,
            items: [
                { label: "Edit property...", icon: <ObliqueIcon name="format_list_bulleted" size={15} />, nextMenu: 'edit_prop' },
                { label: "Add page to...", icon: <ObliqueIcon name="add" size={15} />, value: { type: 'add_page' } },
                { label: "Edit pages in...", icon: <ObliqueIcon name="edit" size={15} />, value: { type: 'edit_pages' } },
                { label: "Send notification to...", icon: <ObliqueIcon name="notifications_none" size={15} />, value: { type: 'notify' } },
                { label: "Send mail to...", icon: <img src="https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg" alt="" style={{width: 14}} />, isNew: true, value: { type: 'mail' } },
                { label: "Send Slack notification to...", icon: <img src="https://upload.wikimedia.org/wikipedia/commons/d/d5/Slack_icon_2019.svg" alt="" style={{width: 14}} />, value: { type: 'slack' } },
                { label: "Define variables", icon: <ObliqueIcon name="description" size={15} />, isNew: true, value: { type: 'variables' } },
            ]
        },
        edit_prop: {
            title: "Edit property", searchable: true,
            items: [
                { label: "Name", icon: <ObliqueIcon name="text_fields" size={15} />, value: { type: 'set_name' } },
                { label: "Date", icon: <ObliqueIcon name="event" size={15} />, nextMenu: 'set_date' },
                { label: "Status", icon: <ObliqueIcon name="radio_button_unchecked" size={15} />, value: { type: 'set_status' } }
            ]
        },
        set_date: {
            title: "Set Date to", searchable: false,
            items: [
                { label: "Time triggered", icon: <ObliqueIcon name="schedule" size={15} />, value: { type: 'set_date_time' } },
                { label: "Date triggered", icon: <ObliqueIcon name="event" size={15} />, value: { type: 'set_date_date' } },
                { label: "Pick a date", icon: <ObliqueIcon name="calendar_today" size={15} />, value: { type: 'set_date_pick' } },
                { label: "Custom formula", icon: <ObliqueIcon name="functions" size={15} />, value: { type: 'formula' } },
            ]
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', pointerEvents: 'auto' }}>
            <motion.div
                initial={{ opacity: 0, scale: 0.98, y: 10, x: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: 10, x: -20 }}
                transition={{ duration: 0.2 }}
                style={{ width: '480px', background: '#ffffff', borderRadius: '10px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: '1px solid var(--border)' }}
            >
                {/* Header matching screenshot 1 */}
                <div style={{ padding: '16px 16px 12px', position: 'relative' }}>
                    <input 
                        value={automationName}
                        onChange={e => setAutomationName(e.target.value)}
                        className="no-focus-glow"
                        style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', width: '90%', padding: 0, margin: '0 0 2px 0', fontFamily: 'inherit' }}
                    />
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        For all pages in
                        <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '2px', color: 'var(--text-secondary)' }}>
                            {activeDatabaseName} <ObliqueIcon name="keyboard_arrow_down" size={16} />
                        </span>
                    </div>
                    
                    <button onClick={onClose} style={{ position: 'absolute', top: '12px', right: '12px', background: 'transparent', border: 'none', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-tertiary)' }} className="hover-bg-subtle">
                        <ObliqueIcon name="close" size={14} />
                    </button>
                </div>
                
                <div style={{ height: '1px', background: 'var(--border-subtle)', margin: '0 16px' }} />

                {/* Body */}
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', minHeight: '240px' }}>
                    
                    {/* TRIGGER BLOCK */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: '500' }}>When</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {triggers.map((t, i) => (
                                <div key={i} style={{ padding: '8px 12px', border: '1px solid var(--border-subtle)', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-primary)' }}>
                                    <ObliqueIcon name="edit" size={15} color="var(--text-tertiary)" /> {t.label}
                                </div>
                            ))}
                            <button
                                onClick={(e) => { setMenuAnchor(e.currentTarget.getBoundingClientRect()); setActiveMenu('trigger'); }}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', background: 'transparent', border: '1px solid var(--border-subtle)', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: '13px', transition: 'all 0.15s', textAlign: 'left' }}
                                className="hover-bg-subtle"
                            >
                                <ObliqueIcon name="add" size={15} />
                                New trigger
                            </button>
                        </div>
                    </div>

                    {/* FAINT VERTICAL LINE */}
                    <div style={{ display: 'flex', padding: '8px 0 8px 24px' }}>
                        <div style={{ width: '1px', height: '30px', background: 'var(--border-subtle)' }} />
                    </div>

                    {/* ACTION BLOCK */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: '500' }}>Do</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {actions.map((a, i) => (
                                <div key={i} style={{ padding: '8px 12px', border: '1px solid var(--border-subtle)', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-primary)' }}>
                                    {a.type === 'formula' ? <ObliqueIcon name="functions" size={15} color="var(--text-tertiary)" /> : <ObliqueIcon name="format_list_bulleted" size={15} color="var(--text-tertiary)" />}
                                    {a.label}
                                </div>
                            ))}
                            <button
                                onClick={(e) => { setMenuAnchor(e.currentTarget.getBoundingClientRect()); setActiveMenu('action'); }}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', background: 'transparent', border: '1px solid var(--border-subtle)', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: '13px', transition: 'all 0.15s', textAlign: 'left' }}
                                className="hover-bg-subtle"
                            >
                                <ObliqueIcon name="add" size={15} />
                                New action
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button onClick={onClose} style={{ padding: '6px 12px', background: 'transparent', border: '1px solid var(--border-subtle)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)' }}>
                        Cancel
                    </button>
                    <button 
                        onClick={() => { onClose(); }}
                        style={{ padding: '6px 12px', background: '#3b82f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: 'white' }}
                    >
                        Create
                    </button>
                </div>
            </motion.div>

            {/* Drilldown Menus */}
            <DrilldownMenu
                isOpen={activeMenu === 'trigger'}
                onClose={() => setActiveMenu(null)}
                anchorRect={menuAnchor}
                menuTree={triggerMenuTree}
                onSelect={(val, item) => setTriggers(prev => [...prev, { ...val, label: item.label }])}
            />
            <DrilldownMenu
                isOpen={activeMenu === 'action'}
                onClose={() => setActiveMenu(null)}
                anchorRect={menuAnchor}
                menuTree={actionMenuTree}
                onSelect={(val, item) => {
                    if (val.type === 'formula') setShowFormula(true);
                    else setActions(prev => [...prev, { ...val, label: item.label }]);
                }}
            />

            <AnimatePresence>
                {showFormula && (
                    <FormulaBuilderModal 
                        isOpen={showFormula} 
                        onClose={() => setShowFormula(false)}
                        onSave={(formulaText) => {
                            setActions(prev => [...prev, { type: 'formula', label: `Formula: ${formulaText}` }]);
                            setShowFormula(false);
                        }}
                    />
                )}
            </AnimatePresence>
        </div>,
        document.body
    );
};

export default AutomationBuilder;
